import { useLayoutEffect, useRef } from "react";
import { theme, FONT_RUNE } from "../theme";
import { drawPiece } from "../pieces";
import { FILES, RANKS, fromKey, type GameState } from "../../engine";

/**
 * Static board renderer for the replay viewer. Same look as BoardCanvas (alternating squares,
 * file/rank labels, drop shadow) but no animations, no selection, no Zustand store — it just
 * draws the GameState it's handed.
 */
export function ReplayBoard({ state }: { state: GameState }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    function draw() {
      if (!canvas || !container) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      const cssW = rect.width;
      const cssH = rect.height;
      // Match BoardCanvas: reserve a small margin on the top and left for the labels.
      const LABEL_MARGIN = 14;
      const usableW = Math.max(0, cssW - LABEL_MARGIN);
      const usableH = Math.max(0, cssH - LABEL_MARGIN);
      const cellW = usableW / FILES;
      const cellH = usableH / RANKS;
      const cell = Math.floor(Math.min(cellW, cellH));
      if (cell <= 0) return;
      const boardW = cell * FILES;
      const boardH = cell * RANKS;
      const offX = LABEL_MARGIN + Math.floor((usableW - boardW) / 2);
      const offY = LABEL_MARGIN + Math.floor((usableH - boardH) / 2);

      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Drop shadow underneath the board (same approach as BoardCanvas.drawBoard).
      ctx.save();
      ctx.fillStyle = theme.boardDark;
      ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
      ctx.shadowBlur = Math.max(8, cell * 0.28);
      ctx.shadowOffsetY = Math.max(2, cell * 0.06);
      ctx.fillRect(offX, offY, boardW, boardH);
      ctx.restore();

      // Squares.
      for (let r = 0; r < RANKS; r++) {
        for (let f = 0; f < FILES; f++) {
          const light = (f + r) % 2 === 0;
          ctx.fillStyle = light ? theme.boardLight : theme.boardDark;
          ctx.fillRect(offX + f * cell, offY + r * cell, cell, cell);
        }
      }

      // Border.
      ctx.strokeStyle = theme.boardEdge;
      ctx.lineWidth = 2;
      ctx.strokeRect(offX, offY, boardW, boardH);

      // File + rank labels.
      ctx.fillStyle = theme.boardLabel;
      const labelSize = Math.max(8, Math.min(12, cell * 0.22));
      ctx.font = `${labelSize}px ${FONT_RUNE}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      for (let f = 0; f < FILES; f++) {
        ctx.fillText("ABCDEFGH"[f], offX + (f + 0.5) * cell, offY - 3);
      }
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      for (let r = 0; r < RANKS; r++) {
        ctx.fillText(String(r + 1), offX - 3, offY + (r + 0.5) * cell);
      }

      // Pieces.
      for (const [k, piece] of state.board) {
        const sq = fromKey(k);
        const cx = offX + (sq.file + 0.5) * cell;
        const cy = offY + (sq.rank + 0.5) * cell;
        drawPiece(ctx, { cell, cx, cy, piece });
      }
    }

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(container);
    return () => ro.disconnect();
  }, [state]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        flex: "1 1 auto",
        minHeight: 0,
        width: "100%",
        // Same aspect-ratio capping trick as the live BoardCanvas — never wider than half the
        // available height, so the board keeps its 1:2 shape regardless of the panel.
        maxWidth: "calc(50cqh + 16px)",
        alignSelf: "center",
      }}
    >
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
    </div>
  );
}
